import { motion } from "framer-motion";
import { SEO } from "@/components/SEO";
import { PublicPageLayout } from "@/components/landing/PublicPageLayout";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

export default function Privacy() {
  return (
    <PublicPageLayout>
      <SEO 
        title="Privacy Policy | Sorvaine"
        description="Sorvaine's privacy policy explains how we collect, use, and protect your data when using our proposal intelligence platform."
        canonical="https://sorvaine.com/privacy"
      />

      <section className="py-20 md:py-28 px-6">
        <motion.div 
          className="max-w-[800px] mx-auto"
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="w-2 h-2 rounded-full bg-primary" />
            <span className="text-[11px] tracking-[0.25em] uppercase text-white/50 font-medium">Legal</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-semibold tracking-[-0.04em] leading-tight mb-4">Privacy Policy</h1>
          <p className="text-white/40 text-sm mb-16">Last updated: March 12, 2026</p>

          <div className="space-y-12">
            <LegalSection title="1. Introduction">
              <p>
                Sorvaine - FZCO ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our proposal intelligence platform and related services.
              </p>
            </LegalSection>

            <LegalSection title="2. Information We Collect">
              <p className="mb-4">We collect information that you provide directly to us, including:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Account information (name, email, company name)</li>
                <li>Proposal content and related documents you upload</li>
                <li>Communication preferences and settings</li>
                <li>Payment and billing information (collected when payment processing is enabled)</li>
                <li>Any other information you choose to provide</li>
              </ul>
            </LegalSection>

            <LegalSection title="3. How We Use Your Information">
              <p className="mb-4">We use the information we collect to:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Provide, maintain, and improve our services</li>
                <li>Process transactions and send related information</li>
                <li>Send technical notices and support messages</li>
                <li>Respond to your comments and questions</li>
                <li>Develop new features and services</li>
                <li>Monitor and analyze usage patterns and trends</li>
              </ul>
              <p className="mt-4">We process your data on the basis of contract performance (to deliver the Service), legitimate interests, and consent where required.</p>
              <p className="mt-4">We also use cookies and similar tracking technologies as described in our Cookie Policy.</p>
            </LegalSection>

            <LegalSection title="4. Data Security">
              <p>
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes encryption of data in transit and at rest, regular security assessments, and access controls. Proposal documents and content library data are stored in isolated, encrypted environments. Your content is never used to train AI models or shared with third parties. Data is stored on Supabase (hosted on AWS). Storage region: us-east-1.
              </p>
            </LegalSection>

            <LegalSection title="5. Data Retention">
              <p>
                We retain account data for the duration of your subscription and for 90 days following cancellation, after which it is permanently deleted. Backup retention may extend up to 30 additional days. We will also retain and use your information as necessary to comply with legal obligations, resolve disputes, and enforce our agreements.
              </p>
            </LegalSection>

            <LegalSection title="6. Your Rights">
              <p className="mb-4">Depending on your location, you may have certain rights regarding your personal information, including:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Access to your personal information</li>
                <li>Correction of inaccurate data</li>
                <li>Deletion of your data</li>
                <li>Data portability</li>
                <li>Opt-out of certain processing activities</li>
              </ul>
              <p className="mt-4">To exercise any of these rights, contact us at{" "}
                <a href="mailto:hello@sorvaine.com?subject=Data%20Request" className="text-primary hover:underline">hello@sorvaine.com</a> with the subject line "Data Request".
              </p>
              <p className="mt-4">If you are located in the United Kingdom, you have the right to lodge a complaint with the Information Commissioner's Office (ICO) at{" "}
                <a href="https://ico.org.uk" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">ico.org.uk</a>.
              </p>
            </LegalSection>

            <LegalSection title="7. Third-Party Services">
              <p>
                Our service may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to read their privacy policies.
              </p>
            </LegalSection>

            <LegalSection title="8. Sub-Processors">
              <p className="mb-4">We use the following third-party sub-processors to deliver the Service:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Supabase (database and authentication) — <a href="https://supabase.com" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">supabase.com</a></li>
                <li>Anthropic (AI processing) — <a href="https://anthropic.com" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">anthropic.com</a></li>
                <li>Stripe (payment processing) — <a href="https://stripe.com" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">stripe.com</a></li>
                <li>Resend (transactional email) — <a href="https://resend.com" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">resend.com</a></li>
                <li>Plausible Analytics (cookieless site analytics, no personal data) — <a href="https://plausible.io" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">plausible.io</a></li>
                <li>Attio (CRM — contact and account management) — <a href="https://attio.com" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">attio.com</a></li>
              </ul>
              <p className="mt-4">These processors are bound by data processing agreements and process data only as necessary to deliver the Service.</p>
            </LegalSection>

            <LegalSection title="9. International Data Transfers and Compliance">
              <p>
                Sorvaine - FZCO operates globally and is committed to compliance with applicable data protection laws. This includes the EU General Data Protection Regulation (GDPR) for users in the European Economic Area, the UAE Federal Decree-Law No. 45 of 2021 on Personal Data Protection (PDPL) for users in the United Arab Emirates, and other applicable regional data protection regulations. Where required, we implement appropriate safeguards for international data transfers.
              </p>
            </LegalSection>

            <LegalSection title="10. Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
              </p>
            </LegalSection>

            <LegalSection title="11. Contact Us">
              <p>
                If you have any questions about this Privacy Policy, please contact us at{" "}
                <a href="mailto:hello@sorvaine.com" className="text-primary hover:underline">hello@sorvaine.com</a>.
              </p>
            </LegalSection>
          </div>
        </motion.div>
      </section>
    </PublicPageLayout>
  );
}

function LegalSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="pb-8 border-b border-white/[0.06] last:border-0">
      <h2 className="text-xl font-semibold tracking-[-0.02em] mb-4 text-white">{title}</h2>
      <div className="text-white/50 text-base leading-[1.8]">{children}</div>
    </section>
  );
}
